import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import TagBar from '@/components/common/TagBar';
import { useI18n } from '@/i18n';

const HelpCenterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<'common' | 'tutor' | 'parent'>(
    initialTab === 'tutor' ? 'tutor' : initialTab === 'parent' ? 'parent' : 'common'
  );
  const [openFAQMap, setOpenFAQMap] = useState<Record<'common' | 'tutor' | 'parent', number | null>>({
    common: null,
    tutor: null,
    parent: null,
  });
  const { t } = useI18n();

  const toggleFAQ = (index: number) => {
    setOpenFAQMap(prev => ({
      ...prev,
      [activeTab]: prev[activeTab] === index ? null : index,
    }));
  };

  const openFAQ = openFAQMap[activeTab];

  const commonQuestions = Array.from({ length: 7 }, (_, index) => ({
    question: t(`helpCenter.common.${index + 1}.question`),
    answer: t(`helpCenter.common.${index + 1}.answer`),
  }));

  const tutorQuestions = Array.from({ length: 6 }, (_, index) => ({
    question: t(`helpCenter.tutor.${index + 1}.question`),
    answer: t(`helpCenter.tutor.${index + 1}.answer`),
  }));

  const parentQuestions = Array.from({ length: 5 }, (_, index) => ({
    question: t(`helpCenter.parent.${index + 1}.question`),
    answer: t(`helpCenter.parent.${index + 1}.answer`),
  }));

  const getCurrentQuestions = () => {
    switch (activeTab) {
      case 'tutor':
        return tutorQuestions;
      case 'parent':
        return parentQuestions;
      default:
        return commonQuestions;
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('helpCenter.hero.title')}</h1>
          <p className="text-xl max-w-3xl mx-auto text-gray-100">
            {t('helpCenter.hero.description')}
          </p>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white shadow-md sticky top-16 z-40">
        <div className="container mx-auto px-4">
          <TagBar
            items={[
              { id: 'common', label: t('helpCenter.tab.common') },
              { id: 'tutor', label: t('helpCenter.tab.tutor') },
              { id: 'parent', label: t('helpCenter.tab.parent') },
            ]}
            activeId={activeTab}
            onChange={setActiveTab}
            className=""
            innerClassName="flex md:justify-center space-x-1 overflow-x-auto scrollbar-hide -mx-4 px-4"
          />
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-8">
              {activeTab === 'common' && t('helpCenter.section.common')}
              {activeTab === 'tutor' && t('helpCenter.section.tutor')}
              {activeTab === 'parent' && t('helpCenter.section.parent')}
            </h2>

            <div className="space-y-4">
              {getCurrentQuestions().map((faq, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-lg pr-8 text-gray-800">{faq.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 text-primary flex-shrink-0 transition-transform ${
                        openFAQ === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFAQ === index && (
                    <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Contact Support */}
            <div className="mt-12 bg-primary-50 border-2 border-primary rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-primary mb-4">{t('helpCenter.contact.title')}</h3>
              <p className="text-gray-700 mb-6">
                {t('helpCenter.contact.description')}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="mailto:tutorfounder@gmail.com"
                  className="btn-primary inline-block"
                >
                  {t('helpCenter.contact.email')}
                </a>
                <a
                  href="https://wa.me/601112049396"
                  className="btn-secondary inline-block"
                >
                  {t('helpCenter.contact.whatsapp')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenterPage;
